import React from "react";
import {
  Page, Text, View, Document, StyleSheet, Image
} from "@react-pdf/renderer";
import PropTypes from "prop-types";

const CONCEPTOS = {
  1: "Enganche", 2: "Mensualidad", 3: "Moratorio", 4: "Pago anticipado",
  5: "Interés", 6: "Pago de contado", 7: "Pago parcial",
  8: "Efectivo", 9: "Transferencia", 10: "Tarjeta"
};

const METODOS = { 1: "Efectivo", 2: "Tarjeta", 3: "Transferencia" };

const fmtMoney = val => "$" + parseFloat(val || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 });
const fmtDate = () => {
  const d = new Date();
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })
    + " " + d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
};

const s = StyleSheet.create({
  page: { padding: 40, backgroundColor: "#fff", fontSize: 10, fontFamily: "Helvetica" },
  // Header
  headerBar: { backgroundColor: "#1565c0", padding: "12 20", borderRadius: 4, marginBottom: 20 },
  headerTitle: { color: "#fff", fontSize: 18, fontWeight: "bold", textAlign: "center" },
  headerSub: { color: "#cce5ff", fontSize: 9, textAlign: "center", marginTop: 2 },
  // Info grid
  infoRow: { flexDirection: "row", marginBottom: 6 },
  infoLabel: { width: 130, color: "#555", fontSize: 9, textTransform: "uppercase" },
  infoValue: { flex: 1, fontSize: 10, fontWeight: "bold" },
  // Divider
  divider: { borderBottomWidth: 1, borderBottomColor: "#ddd", marginVertical: 12 },
  // Section title
  sectionTitle: { fontSize: 11, fontWeight: "bold", color: "#1565c0", marginBottom: 8, textTransform: "uppercase" },
  // Monto destacado
  montoBox: { backgroundColor: "#e8f5e9", borderRadius: 4, padding: "10 16", marginBottom: 14, flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  montoLabel: { fontSize: 11, color: "#333" },
  montoValue: { fontSize: 16, fontWeight: "bold", color: "#2e7d32" },
  // Table
  table: { borderWidth: 1, borderColor: "#ccc", borderRadius: 3, overflow: "hidden" },
  tHead: { flexDirection: "row", backgroundColor: "#f5f5f5" },
  tRow: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#eee" },
  tRowAlt: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#eee", backgroundColor: "#fafafa" },
  tCell: { padding: "4 8", fontSize: 9, textAlign: "center" },
  tCellBold: { padding: "4 8", fontSize: 9, textAlign: "center", fontWeight: "bold" },
  tCellRight: { padding: "4 8", fontSize: 9, textAlign: "right" },
  // QR
  qrContainer: { alignItems: "center", marginTop: 16 },
  qrImage: { width: 80, height: 80 },
  qrText: { fontSize: 7, color: "#999", marginTop: 4 },
  // Footer
  footer: { marginTop: 20, borderTopWidth: 1, borderTopColor: "#ddd", paddingTop: 10, flexDirection: "row", justifyContent: "space-between" },
  footerText: { fontSize: 8, color: "#999" },
  // Firma
  firmaLine: { borderTopWidth: 1, borderTopColor: "#333", width: 180, marginTop: 40, alignSelf: "center" },
  firmaLabel: { fontSize: 9, textAlign: "center", color: "#555", marginTop: 4 }
});

const PurchaseTicket = ({ purchaseData, qrImage }) => {
  const metodo = METODOS[purchaseData.idmetodopago] || "N/A";
  const cajeroNombre = purchaseData.cajeroNombre || "Cajero #" + purchaseData.idCajero;
  const clienteNombre = purchaseData.clienteNombre || "";
  const numContrato = purchaseData.numContrato || "";
  const desarrollo = purchaseData.desarrollo || "";
  const loteDesc = purchaseData.loteDesc || "Lote #" + purchaseData.idLote;
  const montoTotal = parseFloat(purchaseData.MontoPago) || 0;
  const referencia = purchaseData.operaciones && purchaseData.operaciones.length > 0
    ? purchaseData.operaciones[0].referencia : "";

  // Filtrar operaciones de pago (concepto real, no método de pago)
  const opsSalida = (purchaseData.operaciones || []).filter(o => o.naturaleza === "E");

  // Solo las parcialidades que se modificaron en este pago
  const parcialidades = (purchaseData.updatesTblAmort || []).filter(u =>
    opsSalida.some(o => o.idTblAmo === u.id)
  );

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* HEADER */}
        <View style={s.headerBar}>
          <Text style={s.headerTitle}>Recibo de Pago</Text>
          <Text style={s.headerSub}>{fmtDate()}</Text>
        </View>

        {/* INFO DEL PAGO */}
        <View style={s.infoRow}>
          <Text style={s.infoLabel}>No. Contrato:</Text>
          <Text style={s.infoValue}>{numContrato}</Text>
          <Text style={s.infoLabel}>Referencia:</Text>
          <Text style={s.infoValue}>{referencia}</Text>
        </View>
        <View style={s.infoRow}>
          <Text style={s.infoLabel}>Cliente:</Text>
          <Text style={s.infoValue}>{clienteNombre}</Text>
        </View>
        <View style={s.infoRow}>
          <Text style={s.infoLabel}>Desarrollo / Lote:</Text>
          <Text style={s.infoValue}>{desarrollo} — {loteDesc}</Text>
        </View>
        <View style={s.infoRow}>
          <Text style={s.infoLabel}>Cajero:</Text>
          <Text style={s.infoValue}>{cajeroNombre}</Text>
          <Text style={s.infoLabel}>Metodo de pago:</Text>
          <Text style={s.infoValue}>{metodo}</Text>
        </View>

        <View style={s.divider} />

        {/* MONTO TOTAL */}
        <View style={s.montoBox}>
          <Text style={s.montoLabel}>Monto pagado:</Text>
          <Text style={s.montoValue}>{fmtMoney(montoTotal)}</Text>
        </View>

        {/* DESGLOSE DE OPERACIONES */}
        {opsSalida.length > 0 && (
          <>
            <Text style={s.sectionTitle}>Desglose del pago</Text>
            <View style={s.table}>
              <View style={s.tHead}>
                <Text style={[s.tCellBold, { width: "15%" }]}>No.</Text>
                <Text style={[s.tCellBold, { width: "35%" }]}>Concepto</Text>
                <Text style={[s.tCellBold, { width: "25%" }]}>Monto</Text>
                <Text style={[s.tCellBold, { width: "25%" }]}>Referencia</Text>
              </View>
              {opsSalida.map((op, i) => (
                <View style={i % 2 === 0 ? s.tRow : s.tRowAlt} key={i}>
                  <Text style={[s.tCell, { width: "15%" }]}>{op.idTblAmo}</Text>
                  <Text style={[s.tCell, { width: "35%" }]}>{CONCEPTOS[op.concepto] || "Concepto " + op.concepto}</Text>
                  <Text style={[s.tCellRight, { width: "25%" }]}>{fmtMoney(op.monto)}</Text>
                  <Text style={[s.tCell, { width: "25%" }]}>{op.referencia}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        <View style={s.divider} />

        {/* PARCIALIDADES AFECTADAS */}
        {parcialidades.length > 0 && (
          <>
            <Text style={s.sectionTitle}>Parcialidades aplicadas</Text>
            <View style={s.table}>
              <View style={s.tHead}>
                <Text style={[s.tCellBold, { width: "12%" }]}>No.</Text>
                <Text style={[s.tCellBold, { width: "18%" }]}>Estatus</Text>
                <Text style={[s.tCellBold, { width: "22%" }]}>Fecha pago</Text>
                <Text style={[s.tCellBold, { width: "22%" }]}>Vencimiento</Text>
                <Text style={[s.tCellBold, { width: "26%" }]}>Monto</Text>
              </View>
              {parcialidades.map((p, i) => (
                <View style={i % 2 === 0 ? s.tRow : s.tRowAlt} key={i}>
                  <Text style={[s.tCell, { width: "12%" }]}>{p.id}</Text>
                  <Text style={[s.tCell, { width: "18%" }]}>{p.estatus === "A" ? "Pagado" : "Abono"}</Text>
                  <Text style={[s.tCell, { width: "22%" }]}>{p.fechapago || "—"}</Text>
                  <Text style={[s.tCell, { width: "22%" }]}>{p.fechaapagar}</Text>
                  <Text style={[s.tCellRight, { width: "26%" }]}>{fmtMoney(p.montoapagar)}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {/* QR */}
        {qrImage && (
          <View style={s.qrContainer}>
            <Image src={qrImage} style={s.qrImage} />
            <Text style={s.qrText}>{referencia}</Text>
          </View>
        )}

        {/* FIRMA */}
        <View style={s.firmaLine} />
        <Text style={s.firmaLabel}>Firma del cajero</Text>

        {/* FOOTER */}
        <View style={s.footer}>
          <Text style={s.footerText}>Documento generado automaticamente</Text>
          <Text style={s.footerText}>Contrato {numContrato} — {loteDesc}</Text>
        </View>
      </Page>
    </Document>
  );
};

PurchaseTicket.propTypes = {
  purchaseData: PropTypes.object,
  qrImage: PropTypes.string
};

export default PurchaseTicket;
